import { DEFAULT_POLYGON_RPC_URL } from '@/utils/polygon-rpc';

const POLYGON_MAINNET = {
  chainId: 137,
  chainIdHex: '0x89',
  chainName: 'Polygon Mainnet',
  nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  rpcUrls: [String(process.env.VUE_APP_CHAIN_RPC ?? '').trim() || DEFAULT_POLYGON_RPC_URL],
  blockExplorerUrls: ['https://polygonscan.com'],
};

export function getExpectedChainId() {
  const raw = process.env.VUE_APP_CHAIN_ID;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : POLYGON_MAINNET.chainId;
}

export function getExpectedChainIdHex() {
  return `0x${getExpectedChainId().toString(16)}`;
}

function getChainConfig(chainId) {
  if (chainId === 137) return POLYGON_MAINNET;
  return {
    chainId,
    chainIdHex: `0x${chainId.toString(16)}`,
    chainName: process.env.VUE_APP_CHAIN_NAME || `Chain ${chainId}`,
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: [process.env.VUE_APP_CHAIN_RPC || ''],
  };
}

function parseChainId(value) {
  if (typeof value === 'string' && value.startsWith('0x')) {
    return parseInt(value, 16);
  }
  return Number(value);
}

function resolveNumericChainId(chainId) {
  const n = parseChainId(chainId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * 切换到指定 chainId（用于 RC 充值多链）
 * @param {import('ethers').Eip1193Provider} injectedProvider
 * @param {number|string} chainId
 * @param {Object} [meta]
 * @param {string} [meta.chainName]
 * @param {string} [meta.nativeSymbol]
 */
export async function ensureWalletChainById(injectedProvider, chainId, meta = {}) {
  if (!injectedProvider?.request) {
    throw new Error('wallet provider not found');
  }

  const expectedChainId = resolveNumericChainId(chainId);
  if (!expectedChainId) {
    throw new Error('invalid chain id');
  }

  const currentHex = await injectedProvider.request({ method: 'eth_chainId' });
  const currentChainId = parseChainId(currentHex);

  if (currentChainId === expectedChainId) {
    return expectedChainId;
  }

  const targetHex = `0x${expectedChainId.toString(16)}`;

  try {
    await injectedProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: targetHex }],
    });
  } catch (err) {
    if (err?.code === 4902) {
      const fallback = getChainConfig(expectedChainId);
      const chainName = meta.chainName || fallback.chainName;
      const nativeSymbol = meta.nativeSymbol || fallback.nativeCurrency?.symbol || 'ETH';
      await injectedProvider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: targetHex,
            chainName,
            nativeCurrency: {
              name: nativeSymbol,
              symbol: nativeSymbol,
              decimals: fallback.nativeCurrency?.decimals ?? 18,
            },
            rpcUrls: fallback.rpcUrls.filter(Boolean),
            blockExplorerUrls: fallback.blockExplorerUrls || [],
          },
        ],
      });
    } else {
      throw err;
    }
  }

  const afterHex = await injectedProvider.request({ method: 'eth_chainId' });
  const afterChainId = parseChainId(afterHex);
  if (afterChainId !== expectedChainId) {
    throw new Error('wrong network');
  }

  return expectedChainId;
}

/**
 * 确保钱包处于配置的链（默认 Polygon 137），必要时唤起切链/加链
 * @param {import('ethers').Eip1193Provider} injectedProvider
 */
export async function ensureWalletChain(injectedProvider) {
  return ensureWalletChainById(injectedProvider, getExpectedChainId());
}

/** 合约地址在当前链上是否有字节码 */
export async function assertContractDeployed(provider, address, label = 'contract') {
  const addr = String(address ?? '').trim();
  if (!addr) throw new Error(`${label} address missing`);
  const code = await provider.getCode(addr);
  if (!code || code === '0x') {
    throw new Error('wrong network');
  }
}

export function isContractCallDecodeError(err) {
  const msg = String(err?.message ?? err ?? '').toLowerCase();
  return (
    msg.includes('could not decode result data') ||
    msg.includes('bad data') ||
    msg.includes('call exception') ||
    msg.includes('missing revert data')
  );
}
